import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import {
  OPENPOSE_POSE_PAIRS,
  type OpenPoseOverlayPayload,
} from '../../constants/openposeOverlay';
import { mapPointToContainDisplay } from '../../utils/videoOverlayCoords';

type Props = {
  overlay: OpenPoseOverlayPayload;
  sourceWidth: number;
  sourceHeight: number;
  containerWidth: number;
  containerHeight: number;
  videoWidth?: number;
  videoHeight?: number;
  usePxCoords: boolean;
};

const NORMAL_STROKE = 'rgba(0, 255, 200, 0.85)';
const UNUSUAL_STROKE = 'rgba(255, 80, 120, 0.95)';
const JOINT_FILL = 'rgba(255, 235, 80, 0.9)';

function mapPoint(
  x: number,
  y: number,
  sw: number,
  sh: number,
  cw: number,
  ch: number,
  usePx: boolean,
  vidW: number,
  vidH: number
): { x: number; y: number } {
  let sx = x;
  let sy = y;
  let sourceW = sw;
  let sourceH = sh;
  if (vidW > 0 && vidH > 0 && (vidW !== sourceW || vidH !== sourceH)) {
    const scaleX = vidW / sourceW;
    const scaleY = vidH / sourceH;
    sx *= scaleX;
    sy *= scaleY;
    sourceW = vidW;
    sourceH = vidH;
  }
  if (usePx && cw > 0 && ch > 0) {
    return mapPointToContainDisplay(sx, sy, sourceW, sourceH, cw, ch);
  }
  return {
    x: (sx / Math.max(1, sourceW)) * 100,
    y: (sy / Math.max(1, sourceH)) * 100,
  };
}

const OpenPoseSkeletonLayer: React.FC<Props> = ({
  overlay,
  sourceWidth,
  sourceHeight,
  containerWidth,
  containerHeight,
  videoWidth = 0,
  videoHeight = 0,
  usePxCoords,
}) => {
  const elements = useMemo(() => {
    const lines: React.ReactNode[] = [];
    const joints: React.ReactNode[] = [];
    let lineKey = 0;
    let jointKey = 0;
    const sw = Math.max(1, sourceWidth);
    const sh = Math.max(1, sourceHeight);
    const usePx = usePxCoords && containerWidth > 0 && containerHeight > 0;

    overlay.persons.forEach((person, pi) => {
      const byPart = new Map<string, { x: number; y: number }>();
      person.kps.forEach((kp) => {
        const mapped = mapPoint(
          kp.x,
          kp.y,
          sw,
          sh,
          containerWidth,
          containerHeight,
          usePx,
          videoWidth,
          videoHeight
        );
        byPart.set(kp.p, mapped);
      });
      const stroke = person.unusual ? UNUSUAL_STROKE : NORMAL_STROKE;
      const strokeW = person.unusual ? 2.5 : 1.8;
      OPENPOSE_POSE_PAIRS.forEach(([a, b]) => {
        const pa = byPart.get(a);
        const pb = byPart.get(b);
        if (!pa || !pb) return;
        if (usePx) {
          lines.push(
            <line
              key={`l-${pi}-${lineKey++}`}
              x1={pa.x}
              y1={pa.y}
              x2={pb.x}
              y2={pb.y}
              stroke={stroke}
              strokeWidth={strokeW}
              strokeLinecap="round"
            />
          );
        } else {
          lines.push(
            <line
              key={`l-${pi}-${lineKey++}`}
              x1={`${pa.x}%`}
              y1={`${pa.y}%`}
              x2={`${pb.x}%`}
              y2={`${pb.y}%`}
              stroke={stroke}
              strokeWidth={strokeW}
              strokeLinecap="round"
            />
          );
        }
      });
      person.kps.forEach((kp) => {
        const pt = byPart.get(kp.p);
        if (!pt) return;
        const r = person.unusual ? 3.5 : 2.5;
        if (usePx) {
          joints.push(
            <circle
              key={`j-${pi}-${jointKey++}`}
              cx={pt.x}
              cy={pt.y}
              r={r}
              fill={person.unusual ? UNUSUAL_STROKE : JOINT_FILL}
            />
          );
        } else {
          joints.push(
            <circle
              key={`j-${pi}-${jointKey++}`}
              cx={`${pt.x}%`}
              cy={`${pt.y}%`}
              r={r}
              fill={person.unusual ? UNUSUAL_STROKE : JOINT_FILL}
            />
          );
        }
      });
    });
    return { lines, joints };
  }, [
    overlay,
    sourceWidth,
    sourceHeight,
    containerWidth,
    containerHeight,
    videoWidth,
    videoHeight,
    usePxCoords,
  ]);

  const usePx = usePxCoords && containerWidth > 0 && containerHeight > 0;

  return (
    <Box
      component="svg"
      viewBox={usePx ? `0 0 ${containerWidth} ${containerHeight}` : '0 0 100 100'}
      preserveAspectRatio="none"
      sx={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 9,
        overflow: 'visible',
      }}
    >
      {elements.lines}
      {elements.joints}
    </Box>
  );
};

export default OpenPoseSkeletonLayer;
